/**
 * AdminCiroDashboard.jsx
 *
 * Danışman CRM Faz 1 — Ciro Dashboard.
 * Route: /admin/ciro-dashboard
 * Erişim: danisman, admin, superadmin
 *
 * Backend: routes/ciro_dashboard.py
 *   GET /danisman/ciro-dashboard/summary?date=|month=
 *   GET /danisman/ciro-dashboard/danisman-satislari?date=|month=
 *   GET /danisman/hakedis?donem=  (danışman rolü — prim kartı)
 *   GET /danisman/cockpit/summary (mevcut endpoint — "Aktif Hatırlatma" KPI'si için)
 *
 * Rol bazlı şekillendirme SUNUCUDA yapılır — burada client-side maskeleme
 * YOK: bir alan response'ta yoksa o KPI kartı basitçe render edilmez.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import {
  KpiCard, KpiSection,
} from '../components/crm/CrmKpiPrimitives';
import {
  DanismanSatislariCards, HakedisPanel,
} from '../components/crm/DanismanSatislariPanels';
import {
  TrendingUp, Wallet, Users2, Bell, Database, PhoneOff, ShoppingCart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Ana sayfa ──────────────────────────────────────────────────────────────

export default function AdminCiroDashboard() {
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const isDanisman = user?.role === 'danisman';

  const [summary, setSummary] = useState(null);
  const [sLoading, setSLoading] = useState(false);
  const [sError, setSError] = useState(false);

  const [cockpitSummary, setCockpitSummary] = useState(null);

  const fetchSummary = useCallback(async () => {
    setSLoading(true); setSError(false);
    try {
      const r = await axios.get(`${API}/danisman/ciro-dashboard/summary`, { headers: authHeaders() });
      setSummary(r.data);
    } catch (err) { setSError(true); toast.error(getApiErrorMessage(err, lang)); }
    finally { setSLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => {
    axios.get(`${API}/danisman/cockpit/summary`, { headers: authHeaders() })
      .then((r) => setCockpitSummary(r.data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Ciro grubu ──
  const ciroCards = [];
  if (summary?.bugunku_ciro != null) ciroCards.push(<KpiCard key="bugunku" icon={TrendingUp} label={tr('Bugünkü Ciro', "Today's Revenue")} value={fmtMoney(summary.bugunku_ciro)} />);
  if (summary?.aylik_ciro != null) ciroCards.push(<KpiCard key="aylik" icon={TrendingUp} label={tr('Aylık Ciro', 'Monthly Revenue')} value={fmtMoney(summary.aylik_ciro)} />);
  if (summary?.toplam_ciro != null) ciroCards.push(<KpiCard key="toplam" icon={Wallet} label={tr('Toplam Ciro', 'Total Revenue')} value={fmtMoney(summary.toplam_ciro)} />);
  if (summary?.ortalama_sepet_aylik != null) ciroCards.push(<KpiCard key="sepet" icon={ShoppingCart} label={tr('Ortalama Sepet', 'Average Basket')} value={fmtMoney(summary.ortalama_sepet_aylik)} sub={tr('Bu ay', 'This month')} />);

  // ── Satış Aktivitesi grubu ──
  const aktiviteCards = [];
  if (summary?.yeni_satis_adedi_bugun != null) aktiviteCards.push(<KpiCard key="satis-bugun" icon={ShoppingCart} label={tr('Bugünkü Satış Adedi', "Today's Sales Count")} value={summary.yeni_satis_adedi_bugun} />);
  if (summary?.tahsilatli_islem_adedi_aylik != null) aktiviteCards.push(<KpiCard key="satis-aylik" icon={ShoppingCart} label={tr('Aylık Satış Adedi', 'Monthly Sales Count')} value={summary.tahsilatli_islem_adedi_aylik} />);
  if (summary?.benim_satisim) {
    aktiviteCards.push(<KpiCard key="benim-bugun" icon={ShoppingCart} label={tr('Bugünkü Satışınız', 'Your Sales Today')} value={summary.benim_satisim.gunluk?.satis_adedi ?? 0} />);
    aktiviteCards.push(<KpiCard key="benim-aylik" icon={ShoppingCart} label={tr('Aylık Satışınız', 'Your Sales This Month')} value={summary.benim_satisim.aylik?.satis_adedi ?? 0} />);
  }
  if (summary?.bekleyen_odeme) {
    const bo = summary.bekleyen_odeme;
    aktiviteCards.push(
      <KpiCard
        key="bekleyen"
        icon={Wallet}
        label={tr('Bekleyen Ödeme', 'Pending Payment')}
        value={fmtMoney(bo.toplam)}
        sub={bo.gecikmis_adet > 0 ? `${bo.adet} ${tr('adet', 'items')} · ${bo.gecikmis_adet} ${tr('gecikmiş', 'overdue')}` : `${bo.adet} ${tr('adet', 'items')}`}
        subColor={bo.gecikmis_adet > 0 ? 'var(--crm-danger)' : undefined}
      />
    );
  }

  // ── Operasyon grubu ──
  const opCards = [];
  if (summary?.kadro_danisman_sayisi != null) opCards.push(<KpiCard key="kadro" icon={Users2} label={tr('Kadrolu Danışman', 'Staff Consultants')} value={summary.kadro_danisman_sayisi} />);
  if (summary?.bugun_aktif_danisman_sayisi != null) opCards.push(<KpiCard key="aktif-danisman" icon={Users2} label={tr('Bugün Aktif Danışman', 'Active Today')} value={summary.bugun_aktif_danisman_sayisi} />);
  if (cockpitSummary?.yaklasan_hatirlatma_count != null) {
    // Faz 2 tur 2: backend `bugun_hatirlatma_count` (additive, dar kapsamlı —
    // yalnız BUGÜN) eklendi; alan henüz gelmiyorsa (eski backend) eski geniş
    // kapsamlı sayaca defensively düşülür — davranış bozulmaz, sadece etiket
    // güncellenir.
    const hatirlatmaSayisi = cockpitSummary.bugun_hatirlatma_count ?? cockpitSummary.yaklasan_hatirlatma_count;
    opCards.push(<KpiCard key="hatirlatma" icon={Bell} label={tr('Bugünkü Hatırlatma', "Today's Reminders")} value={hatirlatmaSayisi} />);
  }
  if (summary?.toplam_kayit != null) opCards.push(<KpiCard key="kayit" icon={Database} label={tr('Toplam Kayıt', 'Total Leads')} value={summary.toplam_kayit} />);
  if (summary?.aranmayan_data != null) opCards.push(<KpiCard key="aranmayan" icon={PhoneOff} label={tr('Aranmayan Data', 'Uncalled Leads')} value={summary.aranmayan_data} />);

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[rootBreadcrumb(user?.role, tr), { label: tr('Ciro Dashboard', 'Revenue Dashboard') }]}
          title={tr('Ciro Dashboard', 'Revenue Dashboard')}
          subtitle={tr('Tahsilat bazlı ciro göstergeleri ve danışman performansı', 'Collection-based revenue indicators and consultant performance')}
        />

        <KpiSection title={tr('Ciro', 'Revenue')} cols={4} cards={ciroCards} loading={sLoading} error={sError} onRetry={fetchSummary} tr={tr} />
        <KpiSection title={tr('Satış Aktivitesi', 'Sales Activity')} cols={3} cards={aktiviteCards} loading={sLoading} error={sError} onRetry={fetchSummary} tr={tr} />
        <KpiSection title={tr('Operasyon', 'Operations')} cols={4} cards={opCards} loading={sLoading} error={sError} onRetry={fetchSummary} tr={tr} />

        {/* Prim/Hakediş yalnızca danışman rolünde ve ayrı bir endpoint'ten
            gelir (`/danisman/hakedis`) — satış listesi kısmi maskeleme ile
            hem admin hem danışman rolünde AYNI şekilde render edilir (bkz.
            DanismanSatislariPanels.jsx dosya başı yorumu, 2026-08). */}
        {isDanisman && <HakedisPanel authHeaders={authHeaders} lang={lang} tr={tr} />}
        <DanismanSatislariCards authHeaders={authHeaders} lang={lang} tr={tr} />
      </div>
    </AdminLayout>
  );
}
